// src/redux/advisorSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  getAdvisorInfluencer as getAdvisorInfluencerService,
  getAdvisorCompare as getAdvisorCompareService,
  createCampaignAdvisorBrief as createCampaignAdvisorBriefService,
  getLatestCampaignAdvisorBrief as getLatestCampaignAdvisorBriefService,
} from "../services/advisorService";
import { isApiLimitError } from "../services/apiService";

const DEFAULT_UPGRADE_URL = "/pricing";
const DEFAULT_ADVISOR_METER = "ai_advisor_run";
const DEFAULT_COMPARE_ADVISOR_METER = "compare_ai_advisor";
const DEFAULT_BRIEF_ADVISOR_METER = "campaign_advisor_brief";

const initialState = {
  // Single influencer advisor
  influencer: null,
  advisingInfluencer: false,
  advisorInfluencerError: null,
  influencerRequestId: null,

  // Compare advisor
  compare: null,
  advisingCompare: false,
  advisorCompareError: null,
  compareRequestId: null,

  // Campaign brief
  latestBrief: null,
  fetchingBrief: false,
  fetchBriefError: null,
  briefFetchRequestId: null,

  creatingBrief: false,
  createBriefError: null,
  briefCreateRequestId: null,
};

const isObject = (value) =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));

const toErrorMessage = (err, fallback) => {
  if (!err) return fallback;
  if (typeof err === "string") return err;

  return (
    err?.message ||
    err?.error?.message ||
    err?.payload?.message ||
    err?.payload?.error?.message ||
    fallback
  );
};

const errorMessageFromPayload = (payload, fallback) => {
  // Important: no payload means no active error.
  // Returning the fallback here causes stale/false UI error banners after success.
  if (!payload) return "";

  if (typeof payload === "string") return payload;

  return (
    payload?.message ||
    payload?.error?.message ||
    payload?.payload?.message ||
    payload?.payload?.error?.message ||
    fallback
  );
};

const isLimitPayload = (value) =>
  isObject(value) && String(value.type || "").toLowerCase() === "limit";

const toLimitErrorPayload = (
  err,
  fallback,
  fallbackMeter = DEFAULT_ADVISOR_METER
) => ({
  type: "limit",
  kind: err?.kind || "limit_exceeded",
  message: err?.message || fallback,
  status: err?.status || 402,
  code: err?.code || "LIMIT_EXCEEDED",
  reason: err?.reason || err?.code || "LIMIT_EXCEEDED",
  meter: err?.meter || fallbackMeter,
  used: err?.used,
  limit: err?.limit,
  remaining: err?.remaining,
  resetAt: err?.resetAt || err?.reset_at || null,
  upgradeRequired: Boolean(err?.upgradeRequired ?? err?.upgrade_required ?? true),
  upgradeUrl: err?.upgradeUrl || err?.upgrade_url || DEFAULT_UPGRADE_URL,
  payload: err?.payload || null,
});

const toRejectedPayload = (
  err,
  fallback,
  fallbackMeter = DEFAULT_ADVISOR_METER
) => {
  if (isApiLimitError(err)) {
    return toLimitErrorPayload(err, fallback, fallbackMeter);
  }

  if (isLimitPayload(err)) {
    return {
      ...err,
      message: err.message || fallback,
      meter: err.meter || fallbackMeter,
      upgradeUrl: err.upgradeUrl || DEFAULT_UPGRADE_URL,
    };
  }

  return {
    type: "error",
    message: toErrorMessage(err, fallback),
  };
};

// ---------------------------
// Normalizers: UI shape
// ---------------------------
const normalizeAdvisorInfluencer = (raw) => {
  if (!raw || typeof raw !== "object") return null;

  const price = raw.pricing || raw.priceEstimate || raw.price_estimate || null;

  const pricing = price
    ? {
        currency: price.currency || "GBP",
        min: price.min ?? null,
        median: price.median ?? null,
        max: price.max ?? null,
      }
    : null;

  const recommendations =
    Array.isArray(raw.recommendations) && raw.recommendations.length > 0
      ? raw.recommendations
      : Array.isArray(raw.strategy?.deliverables) &&
          raw.strategy.deliverables.length > 0
        ? raw.strategy.deliverables.map((deliverable) => {
            const qty = Number.isFinite(Number(deliverable?.quantity))
              ? Number(deliverable.quantity)
              : 1;

            const type = String(deliverable?.type || "deliverable").trim();

            return {
              title: `${qty}× ${type.toUpperCase()}`,
              reason: deliverable?.notes ? String(deliverable.notes) : "",
            };
          })
        : [];

  const notes =
    raw.notes ??
    raw.negotiation?.rationale ??
    raw.strategy?.cadenceNotes ??
    raw.compliance?.disclaimer ??
    null;

  return {
    ...raw,
    pricing,
    recommendations,
    notes,
  };
};

const normalizeAdvisorCompare = (raw) => {
  if (!raw || typeof raw !== "object") return null;

  const left = raw.left ? normalizeAdvisorInfluencer(raw.left) : raw.left;
  const right = raw.right ? normalizeAdvisorInfluencer(raw.right) : raw.right;

  return {
    ...raw,
    left,
    right,
  };
};

// ---------------------------
// Thunks
// ---------------------------
export const adviseInfluencerAsync = createAsyncThunk(
  "advisor/influencer",
  async (payload, { rejectWithValue }) => {
    try {
      const resp = await getAdvisorInfluencerService(payload);
      return resp || null;
    } catch (error) {
      return rejectWithValue(
        toRejectedPayload(
          error,
          "Failed to run advisor for influencer.",
          DEFAULT_ADVISOR_METER
        )
      );
    }
  }
);

export const adviseCompareAsync = createAsyncThunk(
  "advisor/compare",
  async (payload, { rejectWithValue }) => {
    try {
      const resp = await getAdvisorCompareService(payload);
      return resp || null;
    } catch (error) {
      return rejectWithValue(
        toRejectedPayload(
          error,
          "Failed to run advisor comparison.",
          DEFAULT_COMPARE_ADVISOR_METER
        )
      );
    }
  }
);

export const getLatestBriefAsync = createAsyncThunk(
  "advisor/briefGetLatest",
  async ({ campaignId }, { rejectWithValue }) => {
    try {
      const resp = await getLatestCampaignAdvisorBriefService(campaignId);
      return resp || null;
    } catch (error) {
      return rejectWithValue(
        toRejectedPayload(
          error,
          "Failed to load latest campaign brief.",
          DEFAULT_BRIEF_ADVISOR_METER
        )
      );
    }
  }
);

export const createBriefAsync = createAsyncThunk(
  "advisor/briefCreate",
  async ({ campaignId, payload }, { rejectWithValue }) => {
    try {
      const resp = await createCampaignAdvisorBriefService(campaignId, payload);
      return resp || null;
    } catch (error) {
      return rejectWithValue(
        toRejectedPayload(
          error,
          "Failed to create campaign brief.",
          DEFAULT_BRIEF_ADVISOR_METER
        )
      );
    }
  }
);

// ---------------------------
// Slice
// ---------------------------
const advisorSlice = createSlice({
  name: "advisor",
  initialState,
  reducers: {
    clearAdvisorErrors(state) {
      state.advisorInfluencerError = null;
      state.advisorCompareError = null;
      state.fetchBriefError = null;
      state.createBriefError = null;
    },

    clearAdvisorInfluencerResult(state) {
      state.influencer = null;
      state.advisorInfluencerError = null;
      state.advisingInfluencer = false;
      state.influencerRequestId = null;
    },

    clearAdvisorCompareResult(state) {
      state.compare = null;
      state.advisorCompareError = null;
      state.advisingCompare = false;
      state.compareRequestId = null;
    },

    resetAdvisorState() {
      return { ...initialState };
    },

    clearAdvisorResults(state) {
      state.influencer = null;
      state.compare = null;
      state.advisorInfluencerError = null;
      state.advisorCompareError = null;
      state.influencerRequestId = null;
      state.compareRequestId = null;
      state.advisingInfluencer = false;
      state.advisingCompare = false;
    },

    clearBriefState(state) {
      state.latestBrief = null;
      state.fetchBriefError = null;
      state.createBriefError = null;
      state.fetchingBrief = false;
      state.creatingBrief = false;
      state.briefFetchRequestId = null;
      state.briefCreateRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Influencer advisor
      .addCase(adviseInfluencerAsync.pending, (state, action) => {
        state.advisingInfluencer = true;
        state.advisorInfluencerError = null;
        state.influencerRequestId = action.meta.requestId;
      })
      .addCase(adviseInfluencerAsync.fulfilled, (state, action) => {
        if (state.influencerRequestId !== action.meta.requestId) return;

        state.advisingInfluencer = false;
        state.influencerRequestId = null;
        state.advisorInfluencerError = null;
        state.influencer = normalizeAdvisorInfluencer(action.payload) || null;
      })
      .addCase(adviseInfluencerAsync.rejected, (state, action) => {
        if (state.influencerRequestId !== action.meta.requestId) return;

        state.advisingInfluencer = false;
        state.influencerRequestId = null;
        state.advisorInfluencerError =
          action.payload ||
          action.error?.message ||
          "Failed to run advisor for influencer.";
      })

      // Compare advisor
      .addCase(adviseCompareAsync.pending, (state, action) => {
        state.advisingCompare = true;
        state.advisorCompareError = null;
        state.compareRequestId = action.meta.requestId;
      })
      .addCase(adviseCompareAsync.fulfilled, (state, action) => {
        if (state.compareRequestId !== action.meta.requestId) return;

        state.advisingCompare = false;
        state.compareRequestId = null;
        state.advisorCompareError = null;
        state.compare = normalizeAdvisorCompare(action.payload) || null;
      })
      .addCase(adviseCompareAsync.rejected, (state, action) => {
        if (state.compareRequestId !== action.meta.requestId) return;

        state.advisingCompare = false;
        state.compareRequestId = null;
        state.advisorCompareError =
          action.payload ||
          action.error?.message ||
          "Failed to run advisor comparison.";
      })

      // Get latest campaign brief
      .addCase(getLatestBriefAsync.pending, (state, action) => {
        state.fetchingBrief = true;
        state.fetchBriefError = null;
        state.briefFetchRequestId = action.meta.requestId;
      })
      .addCase(getLatestBriefAsync.fulfilled, (state, action) => {
        if (state.briefFetchRequestId !== action.meta.requestId) return;

        state.fetchingBrief = false;
        state.briefFetchRequestId = null;
        state.fetchBriefError = null;
        state.latestBrief = action.payload || null;
      })
      .addCase(getLatestBriefAsync.rejected, (state, action) => {
        if (state.briefFetchRequestId !== action.meta.requestId) return;

        state.fetchingBrief = false;
        state.briefFetchRequestId = null;
        state.fetchBriefError =
          action.payload ||
          action.error?.message ||
          "Failed to load latest campaign brief.";
      })

      // Create campaign brief
      .addCase(createBriefAsync.pending, (state, action) => {
        state.creatingBrief = true;
        state.createBriefError = null;
        state.briefCreateRequestId = action.meta.requestId;
      })
      .addCase(createBriefAsync.fulfilled, (state, action) => {
        if (state.briefCreateRequestId !== action.meta.requestId) return;

        state.creatingBrief = false;
        state.briefCreateRequestId = null;
        state.createBriefError = null;
        state.latestBrief = action.payload || state.latestBrief;
      })
      .addCase(createBriefAsync.rejected, (state, action) => {
        if (state.briefCreateRequestId !== action.meta.requestId) return;

        state.creatingBrief = false;
        state.briefCreateRequestId = null;
        state.createBriefError =
          action.payload ||
          action.error?.message ||
          "Failed to create campaign brief.";
      });
  },
});

export const {
  clearAdvisorErrors,
  clearAdvisorInfluencerResult,
  clearAdvisorCompareResult,
  resetAdvisorState,
  clearAdvisorResults,
  clearBriefState,
} = advisorSlice.actions;

// ---------------------------
// Selectors
// ---------------------------
export const selectAdvisorInfluencer = (state) =>
  state.advisor?.influencer || null;

export const selectAdvisingInfluencer = (state) =>
  state.advisor?.advisingInfluencer || false;

export const selectAdvisorInfluencerError = (state) =>
  state.advisor?.advisorInfluencerError || null;

export const selectAdvisorInfluencerErrorMessage = (state) =>
  errorMessageFromPayload(
    state.advisor?.advisorInfluencerError,
    "Failed to run advisor for influencer."
  );

export const selectAdvisorCompare = (state) => state.advisor?.compare || null;

export const selectAdvisingCompare = (state) =>
  state.advisor?.advisingCompare || false;

export const selectAdvisorCompareError = (state) =>
  state.advisor?.advisorCompareError || null;

export const selectAdvisorCompareErrorMessage = (state) =>
  errorMessageFromPayload(
    state.advisor?.advisorCompareError,
    "Failed to run advisor comparison."
  );

export const selectLatestAdvisorBrief = (state) =>
  state.advisor?.latestBrief || null;

export const selectFetchingAdvisorBrief = (state) =>
  state.advisor?.fetchingBrief || false;

export const selectAdvisorBriefError = (state) =>
  state.advisor?.fetchBriefError || null;

export const selectAdvisorBriefErrorMessage = (state) =>
  errorMessageFromPayload(
    state.advisor?.fetchBriefError,
    "Failed to load latest campaign brief."
  );

export const selectCreatingAdvisorBrief = (state) =>
  state.advisor?.creatingBrief || false;

export const selectCreateAdvisorBriefError = (state) =>
  state.advisor?.createBriefError || null;

export const selectCreateAdvisorBriefErrorMessage = (state) =>
  errorMessageFromPayload(
    state.advisor?.createBriefError,
    "Failed to create campaign brief."
  );

export const selectAdvisorLimitError = (state) => {
  const candidates = [
    state.advisor?.advisorInfluencerError,
    state.advisor?.advisorCompareError,
    state.advisor?.createBriefError,
    state.advisor?.fetchBriefError,
  ];

  return candidates.find(isLimitPayload) || null;
};

export default advisorSlice.reducer;
